<template>
  <div class="customers-page">
    <PageHeader
      :title="text.page.title"
      :subtitle="text.page.subtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    />

    <div class="customers-content">
      <section class="metrics-grid">
        <CustomersKpiCard
          v-for="metric in visibleMetrics"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-meta="metric.valueMeta"
          :truncate-value="metric.truncateValue"
          :value-scale="metric.valueScale"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>

      <CustomersToolbar
        @filters-change="handleFiltersChange"
        @search-change="handleSearchChange"
        @apply="applyToolbarFilters"
        @toggle-view="toggleViewMode"
      />

      <CustomersTable
        :rows="customersRows"
        :loading="tableLoading"
        :pagination="customersPagination"
        :capabilities="tableCapabilities"
        :sort-field="customersSort.field"
        :sort-direction="customersSort.direction"
        :empty-text="text.table.empty"
        @edit-customer="handleEditCustomer"
        @delete-customer="handleDeleteCustomer"
        @toggle-customer-status="handleToggleCustomerStatus"
        @page-change="handlePageChange"
        @sort-change="handleSortChange"
      />
    </div>

    <CustomerModal
      :show="isCreateCustomerModalOpen"
      @close="closeCreateCustomerModal"
      @submit="handleCreateCustomer"
    />

    <CustomerModal
      :show="isEditCustomerModalOpen"
      mode="edit"
      :initial-data="editCustomerData"
      @close="closeEditCustomerModal"
      @delete="handleDeleteCustomerFromModal"
      @submit="handleUpdateCustomer"
    />

    <CustomerImportModal
      :show="isCustomerImportModalOpen"
      @close="closeCustomerImportModal"
      @success="handleCustomerImportSuccess"
    />

    <ExportFormatModal
      :show="isCustomerExportModalOpen"
      :filters="activeFilters"
      :search-term="searchTerm"
      :page-customer-ids="currentPageCustomerIds"
      :text-catalog="text"
      :export-service="exportService"
      file-name-prefix="clientes"
      @close="closeCustomerExportModal"
    />

    <ConfirmActionModal
      :show="isConfirmActionModalOpen"
      :tone="confirmActionTone"
      :loading="isConfirmActionLoading"
      :title="confirmActionTitle"
      :subtitle="confirmActionSubtitle"
      :message="confirmActionMessage"
      :confirm-text="confirmActionButton"
      :cancel-text="text.actions.common.cancel"
      @confirm="confirmPendingAction"
      @cancel="closeConfirmActionModal"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import CustomersKpiCard from '@/components/customers/CustomersKpiCard.vue';
import CustomersToolbar from '@/components/customers/CustomersToolbar.vue';
import CustomersTable from '@/components/customers/CustomersTable.vue';
import CustomerModal from '@/components/customers/CustomerModal.vue';
import CustomerImportModal from '@/components/customers/imports/CustomerImportModal.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import customersService from '@/services/customers';
import { CAPABILITIES_UPDATED_EVENT } from '@/utils/capabilities';
import {
  createDefaultCustomerFilters,
  normalizeCustomerFilters,
  buildCustomersListParams,
  filterCustomersRowsLocal,
  buildCustomersMetricsParams,
} from '@/composables/customers/useCustomersFilters';
import { fetchCustomersListPayload } from '@/composables/customers/useCustomersList';
import {
  createInitialCustomersMetricsValues,
  createInitialCustomersMetricsVisibility,
  fetchCustomersMetricsPayload,
  buildCustomersKpiCards,
} from '@/composables/customers/useCustomersMetrics';
import { buildPaginationPayload, resolvePageChange, sliceRowsByPage } from '@/composables/shared/usePagination';
import { resolveSortChange, sortRows } from '@/composables/shared/useSorting';
import { extractPayload } from '@/utils/dataNormalization';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');
const CUSTOMERS_SYNC_COOLDOWN_MS = 60 * 60 * 1000;

export default {
  name: 'CustomersPage',
  components: {
    PageHeader,
    CustomersKpiCard,
    CustomersToolbar,
    CustomersTable,
    CustomerModal,
    CustomerImportModal,
    ExportFormatModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      customersService,
      isCreateCustomerModalOpen: false,
      isEditCustomerModalOpen: false,
      isCustomerImportModalOpen: false,
      isCustomerExportModalOpen: false,
      editCustomerId: 0,
      editCustomerData: null,
      isConfirmActionModalOpen: false,
      pendingActionType: null,
      pendingActionCustomer: null,
      isCustomerSyncRunning: false,
      headerActionsBase: [
        {
          key: 'new-customer',
          label: text.actions.header.newCustomer,
          icon: 'fa-plus',
          variant: 'primary',
        },
        {
          key: 'import-customers',
          label: text.actions.header.importCustomers,
          icon: 'fa-upload',
          variant: 'secondary',
        },
        {
          key: 'export-customers',
          label: text.actions.header.exportCustomers,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'sync-customers',
          label: text.actions.header.syncCustomers,
          icon: 'fa-refresh',
          variant: 'secondary',
          className: 'app-header-btn--sync',
          iconOnly: true,
          title: text.confirm.sync.title,
        },
      ],
      headerActions: [],
      customersAllRows: [],
      customersRows: [],
      customersPagination: {
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      },
      customersSort: {
        field: 'name',
        direction: 'asc',
      },
      tableLoading: false,
      viewMode: 'table',
      searchTerm: '',
      activeFilters: createDefaultCustomerFilters(),
      metricsLoading: false,
      metricsValues: createInitialCustomersMetricsValues(),
      metricsVisibility: createInitialCustomersMetricsVisibility(),
    };
  },
  computed: {
    exportService() {
      return customersService;
    },
    tableCapabilities() {
      const can = typeof this.$can === 'function' ? this.$can : () => false;

      return {
        canViewDetail: can('customers.detail'),
        canDelete: can('customers.delete'),
        canEdit: can('customers.edit'),
        canViewSales: can('customers.view_sales'),
        canViewTotal: can('customers.view_total'),
      };
    },
    canShowSalesMetrics() {
      return this.tableCapabilities.canViewSales && this.metricsVisibility.can_view_sales;
    },
    canShowTotalMetrics() {
      return this.tableCapabilities.canViewTotal && this.metricsVisibility.can_view_total;
    },
    visibleMetrics() {
      return buildCustomersKpiCards(this.metricsValues, {
        can_view_sales: this.canShowSalesMetrics,
        can_view_total: this.canShowTotalMetrics,
      });
    },
    currentPageCustomerIds() {
      return (Array.isArray(this.customersRows) ? this.customersRows : [])
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0);
    },
    confirmActionTitle() {
      if (this.pendingActionType === 'sync-customers') {
        return text.confirm.sync.title;
      }

      if (this.pendingActionType === 'toggle-status') {
        const willDisable = Boolean(this.pendingActionCustomer?.active);
        return willDisable ? text.confirm.toggleStatus.titleDisable : text.confirm.toggleStatus.titleActivate;
      }

      return text.confirm.delete.title;
    },
    confirmActionSubtitle() {
      if (this.pendingActionType === 'sync-customers') {
        return text.confirm.sync.subtitle;
      }

      if (this.pendingActionType === 'toggle-status') {
        return text.confirm.toggleStatus.subtitle;
      }

      return text.confirm.delete.subtitle;
    },
    confirmActionMessage() {
      if (this.pendingActionType === 'sync-customers') {
        return text.confirm.sync.message;
      }

      const customerName = String(this.pendingActionCustomer?.name || '').trim() || text.common.noName;

      if (this.pendingActionType === 'toggle-status') {
        const willDisable = Boolean(this.pendingActionCustomer?.active);
        return willDisable
          ? text.confirm.toggleStatus.messageDisable(customerName)
          : text.confirm.toggleStatus.messageActivate(customerName);
      }

      return text.confirm.delete.message(customerName);
    },
    confirmActionButton() {
      if (this.pendingActionType === 'sync-customers') {
        return text.confirm.sync.confirm;
      }

      if (this.pendingActionType === 'toggle-status') {
        return this.pendingActionCustomer?.active ? text.actions.common.disable : text.actions.common.activate;
      }

      return text.actions.common.delete;
    },
    confirmActionTone() {
      if (this.pendingActionType === 'sync-customers') {
        return 'info';
      }

      return 'warning';
    },
    isConfirmActionLoading() {
      return this.pendingActionType === 'sync-customers' && this.isCustomerSyncRunning;
    },
  },
  mounted() {
    this.refreshHeaderActions();
    window.addEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
    this.fetchCustomers();
    this.fetchMetrics();
  },
  beforeUnmount() {
    window.removeEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
  },
  methods: {
    buildSortedCustomerRows(rows) {
      return sortRows(rows, this.customersSort, {
        name: (row) => row?.name,
        sales: (row) => Number(row?.sales || 0),
        income_forecast: (row) => Number(row?.income_forecast || 0),
        income_billed: (row) => Number(row?.income_billed || 0),
      });
    },
    buildCustomersParams() {
      return buildCustomersListParams({
        searchTerm: '',
        filters: this.activeFilters,
      });
    },
    buildFilteredCustomerRows(rows) {
      return filterCustomersRowsLocal(rows, this.searchTerm);
    },
    syncCustomerRowsPage() {
      const filteredRows = this.buildFilteredCustomerRows(this.customersAllRows);
      const sortedRows = this.buildSortedCustomerRows(filteredRows);
      const pagination = buildPaginationPayload({
        currentPage: this.customersPagination.current_page,
        pageSize: this.customersPagination.per_page,
        totalItems: sortedRows.length,
      });

      this.customersPagination = pagination;
      this.customersRows = sliceRowsByPage(
        sortedRows,
        pagination.current_page,
        pagination.per_page,
      );
    },
    buildMetricsParams() {
      return buildCustomersMetricsParams(this.activeFilters);
    },
    async fetchMetrics() {
      this.metricsLoading = true;

      try {
        const metricsPayload = await fetchCustomersMetricsPayload(customersService, this.buildMetricsParams());
        this.metricsValues = metricsPayload.values;
        this.metricsVisibility = metricsPayload.visibility;
      } catch (error) {
        this.metricsValues = createInitialCustomersMetricsValues();
        this.metricsVisibility = createInitialCustomersMetricsVisibility();
      } finally {
        this.metricsLoading = false;
      }
    },
    async fetchCustomers() {
      this.tableLoading = true;

      try {
        const payload = await fetchCustomersListPayload(
          customersService,
          this.buildCustomersParams(),
          this.customersPagination,
        );
        this.customersAllRows = Array.isArray(payload.data) ? payload.data : [];
        this.syncCustomerRowsPage();
      } catch (error) {
        this.customersAllRows = [];
        this.customersRows = [];
        this.customersPagination = {
          ...this.customersPagination,
          current_page: 1,
          total_pages: 1,
          total_items: 0,
        };
      } finally {
        this.tableLoading = false;
      }
    },
    refreshHeaderActions() {
      const filteredActions = this.headerActionsBase.filter((action) => {
        if (action?.key === 'new-customer') {
          return this.$can('customers.create');
        }

        if (action?.key === 'sync-customers') {
          return this.$can('customers.edit');
        }

        return true;
      });

      this.headerActions = filteredActions.map((action) => {
        if (action?.key !== 'sync-customers') {
          return action;
        }

        return {
          ...action,
          loading: this.isCustomerSyncRunning,
          disabled: this.isCustomerSyncRunning,
        };
      });
    },
    onHeaderAction(action) {
      if ((action?.key || '') === 'new-customer') {
        this.isCreateCustomerModalOpen = true;
      }

      if ((action?.key || '') === 'import-customers') {
        this.isCustomerImportModalOpen = true;
      }

      if ((action?.key || '') === 'export-customers') {
        this.isCustomerExportModalOpen = true;
      }

      if ((action?.key || '') === 'sync-customers') {
        this.openCustomerSyncConfirmModal();
      }
    },
    resolveCustomerSyncStorageKey() {
      const tenantKey = String(window?.location?.hostname || '').trim() || 'global';
      return `customers.last_sync_financials_at.${tenantKey}`;
    },
    readLastCustomerSyncAt() {
      const raw = localStorage.getItem(this.resolveCustomerSyncStorageKey());
      const parsed = Number(raw || 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    writeLastCustomerSyncAt(timestamp) {
      localStorage.setItem(this.resolveCustomerSyncStorageKey(), String(Number(timestamp || 0)));
    },
    resolveCustomerSyncRemainingMinutes() {
      const lastAt = this.readLastCustomerSyncAt();
      if (lastAt <= 0) {
        return 0;
      }

      const elapsed = Date.now() - lastAt;
      const remaining = CUSTOMERS_SYNC_COOLDOWN_MS - elapsed;
      if (remaining <= 0) {
        return 0;
      }

      return Math.max(1, Math.ceil(remaining / (60 * 1000)));
    },
    canRunCustomerSyncNow() {
      return this.resolveCustomerSyncRemainingMinutes() <= 0;
    },
    openCustomerSyncConfirmModal() {
      if (this.isCustomerSyncRunning) {
        return;
      }

      if (!this.canRunCustomerSyncNow()) {
        const minutes = this.resolveCustomerSyncRemainingMinutes();
        this.$toast?.info(text.toast.syncCooldown(minutes));
        return;
      }

      this.openConfirmActionModal('sync-customers', null);
    },
    closeCustomerImportModal() {
      this.isCustomerImportModalOpen = false;
    },
    closeCustomerExportModal() {
      this.isCustomerExportModalOpen = false;
    },
    handleCustomerImportSuccess() {
      this.fetchCustomers();
      this.fetchMetrics();
    },
    closeCreateCustomerModal() {
      this.isCreateCustomerModalOpen = false;
    },
    closeEditCustomerModal() {
      this.isEditCustomerModalOpen = false;
      this.editCustomerId = 0;
      this.editCustomerData = null;
    },
    handleSearchChange(search) {
      this.searchTerm = String(search || '').trim();

      this.customersPagination = {
        ...this.customersPagination,
        current_page: 1,
      };

      this.syncCustomerRowsPage();
    },
    handleFiltersChange(filters) {
      const nextFilters = normalizeCustomerFilters({
        ...this.activeFilters,
        ...(filters || {}),
      });
      this.activeFilters = nextFilters;
    },
    applyToolbarFilters() {
      this.customersPagination = {
        ...this.customersPagination,
        current_page: 1,
      };

      this.fetchCustomers();
      this.fetchMetrics();
    },
    handlePageChange(nextPage) {
      const validPage = resolvePageChange(
        nextPage,
        this.customersPagination.total_pages,
        this.customersPagination.current_page,
      );

      if (validPage === this.customersPagination.current_page) {
        return;
      }

      this.customersPagination = {
        ...this.customersPagination,
        current_page: validPage,
      };
      this.syncCustomerRowsPage();
    },
    handleSortChange(sortPayload) {
      const nextSort = resolveSortChange(this.customersSort, sortPayload, [
        'name',
        'sales',
        'income_forecast',
        'income_billed',
      ]);

      if (
        nextSort.field === this.customersSort.field
        && nextSort.direction === this.customersSort.direction
      ) {
        return;
      }

      this.customersSort = nextSort;

      this.customersPagination = {
        ...this.customersPagination,
        current_page: 1,
      };

      this.syncCustomerRowsPage();
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'table-alt' : 'table';
    },
    handleDeleteCustomer(customer) {
      if (!customer?.id) {
        return;
      }

      this.openConfirmActionModal('delete', customer);
    },
    handleDeleteCustomerFromModal(customer) {
      if (!customer?.id) {
        return;
      }

      this.closeEditCustomerModal();
      this.openConfirmActionModal('delete', customer);
    },
    async handleEditCustomer(customer) {
      const customerId = Number(customer?.id || 0);
      if (customerId <= 0) {
        return;
      }

      try {
        const response = await customersService.show(customerId);
        this.editCustomerData = extractPayload(response);
        this.editCustomerId = customerId;
        this.isEditCustomerModalOpen = true;
      } catch (error) {
        const message = error?.response?.data?.message || text.toast.loadCustomerError;
        this.$toast?.error(message);
      }
    },
    openConfirmActionModal(actionType, customer) {
      this.pendingActionType = String(actionType || '');
      this.pendingActionCustomer = customer || null;
      this.isConfirmActionModalOpen = true;
    },
    closeConfirmActionModal() {
      if (this.pendingActionType === 'sync-customers' && this.isCustomerSyncRunning) {
        return;
      }

      this.isConfirmActionModalOpen = false;
      this.pendingActionType = null;
      this.pendingActionCustomer = null;
    },
    async confirmPendingAction() {
      if (this.pendingActionType === 'sync-customers') {
        await this.confirmCustomerSyncAction();
        return;
      }

      const customer = this.pendingActionCustomer;
      if (!customer?.id) {
        this.closeConfirmActionModal();
        return;
      }

      let success = false;

      try {
        if (this.pendingActionType === 'toggle-status') {
          const nextStatus = customer.active ? 0 : 1;
          await customersService.toggleStatus(customer.id, { active: nextStatus });
          this.$toast?.success(nextStatus === 1 ? text.toast.activateSuccess : text.toast.disableSuccess);
        } else {
          await customersService.delete(customer.id);
          this.$toast?.success(text.toast.deleteSuccess);
        }
        success = true;
      } catch (error) {
        const message = error?.response?.data?.message || text.toast.actionError;
        this.$toast?.error(message);
      } finally {
        this.closeConfirmActionModal();

        if (success) {
          this.fetchCustomers();
          this.fetchMetrics();
        }
      }
    },
    async confirmCustomerSyncAction() {
      if (this.isCustomerSyncRunning) {
        return;
      }

      if (!this.canRunCustomerSyncNow()) {
        const minutes = this.resolveCustomerSyncRemainingMinutes();
        this.closeConfirmActionModal();
        this.$toast?.info(text.toast.syncCooldown(minutes));
        return;
      }

      this.isCustomerSyncRunning = true;
      this.refreshHeaderActions();

      try {
        const response = await customersService.syncFinancials();

        this.writeLastCustomerSyncAt(Date.now());
        this.$toast?.success(response?.message || text.toast.syncSuccess);
        this.fetchCustomers();
        this.fetchMetrics();
      } catch (error) {
        const message = error?.response?.data?.message || text.toast.syncError;
        this.$toast?.error(message);
      } finally {
        this.isCustomerSyncRunning = false;
        this.refreshHeaderActions();
        this.closeConfirmActionModal();
      }
    },
    handleToggleCustomerStatus(customer) {
      if (!customer?.id) {
        return;
      }

      this.openConfirmActionModal('toggle-status', customer);
    },
    async handleCreateCustomer(payload) {
      try {
        const response = await customersService.store(payload || {});

        this.closeCreateCustomerModal();
        this.fetchCustomers();
        this.fetchMetrics();
        this.$toast?.success(response?.message || text.toast.createSuccess);
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.createError;
        this.$toast?.error(message);
      }
    },
    async handleUpdateCustomer(payload) {
      if (!this.editCustomerId) {
        return;
      }

      try {
        const response = await customersService.update(this.editCustomerId, payload || {});

        this.closeEditCustomerModal();
        this.fetchCustomers();
        this.fetchMetrics();
        this.$toast?.success(response?.data?.message || text.toast.updateSuccess);
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.updateError;
        this.$toast?.error(message);
      }
    },
  },
};
</script>
